import { firebaseApp } from '@upflowy/firestore';
import { ConsumerAnalyticsService, logger } from '@upflowy/logger';
import { AppMode, ConsumerImplementation } from '@upflowy/types';
import { getAuth, signInAnonymously } from 'firebase/auth';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { useNetworkErrorHandler } from './network-error-handler-provider';

import Loading from '../components/Loading';
import { getUpflowyURLParams, UpflowyURLParams } from '../helpers';
import { getUtmParams } from '../helpers/get-params';
import { isStandalone } from '../helpers/is-standalone';
import { withOfflineExceptionAsync } from '../helpers/with-offline-exception';
import { useRedirect } from '../hooks/useRedirect';
import { getLocation } from '../services/callable-functions';

export type ILocation = {
  ipAddress?: string | null;
  country?: string | null;
  city?: string | null;
  region?: string | null;
};

type AnonymousUser = {
  uid: string;
  idToken: string;
  location: ILocation;
};

type AppContextType = {
  [key: string]: unknown;
  containerId: string | null;
  origin: string | null;
  mode: AppMode;
  isStandaloneApp: boolean;
  urlParams: UpflowyURLParams;
  utmParams: Record<string, string>;
  user: AnonymousUser | null;
  implementation: ConsumerImplementation;
  notFoundURL: string | null;
  resourceNotAvailableURL: string | null;
};

const truthyOrNull = (param: string | null) => (param && param !== '' ? param : null);

export const AppContext = React.createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const ctx = useContext(AppContext);

  if (!ctx) {
    throw new Error('Context AppContext not defined. Provider is required for context');
  }

  return ctx;
};

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [anonymousUser, setAnonymousUser] = useState<AnonymousUser | null>(null);
  const [networkError, setNetworkError] = useState(false);
  const { networkErrorHandler, retryCount } = useNetworkErrorHandler();
  const handleRedirect = useRedirect();

  const retrievedAnonymousUser = useMemo(() => anonymousUser?.uid, [anonymousUser]);

  useEffect(() => {
    const getUser = async () => {
      const credential = await signInAnonymously(getAuth(firebaseApp));

      if (credential.user) {
        const { uid } = credential.user;
        logger.setUser({ id: uid });

        const idToken = await credential.user.getIdToken();

        await getLocation().then(({ data: location }) => {
          setAnonymousUser({
            uid,
            idToken,
            location,
          });
        });
      }
    };

    withOfflineExceptionAsync(getUser)().catch((err) => {
      networkErrorHandler(err, () => {
        setNetworkError(!retrievedAnonymousUser);
      });
    });
  }, [networkErrorHandler, retrievedAnonymousUser, retryCount]);

  const location = window.location.toString();
  const url = new URL(location);
  const containerId = url.searchParams.get('containerId');
  const origin = url.searchParams.get('origin');
  const mode = isStandalone(url);
  const notFoundURL = url.searchParams.get('404');
  const resourceNotAvailableURL = url.searchParams.get('409');
  const implementation: ConsumerImplementation =
    (url.searchParams.get('implementation') as ConsumerImplementation) ?? 'live';
  const urlParams: UpflowyURLParams = getUpflowyURLParams(url.searchParams);
  const utmParams = getUtmParams(url.searchParams);

  const value: AppContextType = {
    containerId,
    origin,
    mode,
    isStandaloneApp: mode === 'standalone',
    urlParams,
    user: null,
    implementation,
    utmParams,
    notFoundURL: truthyOrNull(notFoundURL),
    resourceNotAvailableURL: truthyOrNull(resourceNotAvailableURL),
  };

  logger.setContext('AppProvider', value);
  ConsumerAnalyticsService.setUpflowyTags({
    implementation,
    mode,
    origin: origin !== null ? origin : undefined,
  });

  useEffect(() => {
    if (networkError && !window.location.pathname.startsWith('/offline')) {
      handleRedirect(`/offline?returnTo=${window.location.pathname}`);
    }
  });

  return (
    <>
      {!anonymousUser && <Loading />}
      {anonymousUser && (
        <AppContext.Provider
          value={{
            ...value,
            user: anonymousUser,
          }}
        >
          {children}
        </AppContext.Provider>
      )}
    </>
  );
};
